import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import ScadenzeBanner from "@/components/ScadenzeBanner";
import { PresentazioneAdmin } from "@/components/PresentazioneAdmin";
import { StatoVerbaleBadge } from "@/components/StatoBadge";
import { Link } from "react-router-dom";
import { Users, UserX, FileText, CalendarClock, BellRing, ArrowRight, Euro, FileDown } from "lucide-react";
import { fmtDateTime } from "@/lib/constants";
import { ConfrontoProvince } from "@/components/ConfrontoProvince";

const STAT_CARDS = [
  { key: "guardie_attive", label: "Guardie attive", icon: Users, color: "text-green-600", bg: "bg-green-50", to: "/anagrafica?stato=attiva", roles: ["admin", "coordinatore"] },
  { key: "guardie_disattivate", label: "Non attive", icon: UserX, color: "text-slate-500", bg: "bg-slate-100", to: "/anagrafica?stato=disattivata", roles: ["admin", "coordinatore"] },
  { key: "interventi_mese", label: "Verbali (mese)", icon: FileText, color: "text-blue-600", bg: "bg-blue-50", to: "/interventi" },
  { key: "turni_oggi", label: "Turni oggi", icon: CalendarClock, color: "text-[#166534]", bg: "bg-emerald-50", to: "/turni" },
  { key: "scadenze_30gg", label: "Scadenze 30gg", icon: BellRing, color: "text-amber-600", bg: "bg-amber-50", to: "/scadenze", roles: ["admin", "coordinatore", "territoriale", "com_distaccamento"] },
];

const StatCard = ({ c, value, clickable }) => {
  const body = (
    <Card data-testid={`stat-${c.key}`} className={`h-full border-slate-200 shadow-sm transition-[box-shadow,transform] hover:shadow-md ${clickable ? "group-hover:-translate-y-0.5 group-hover:border-[#166534]/40" : ""}`}>
      <CardContent className="relative p-5">
        {clickable && <ArrowRight className="absolute right-4 top-5 h-4 w-4 text-slate-300 transition-[color,transform] group-hover:translate-x-0.5 group-hover:text-[#166534]" />}
        <div className={`mb-3 inline-flex h-10 w-10 items-center justify-center rounded-lg ${c.bg}`}>
          <c.icon className={`h-5 w-5 ${c.color}`} />
        </div>
        <p className="font-heading text-2xl font-extrabold text-slate-900">{value}</p>
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{c.label}</p>
      </CardContent>
    </Card>
  );
  return clickable
    ? <Link to={c.to} className="group block rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[#166534]" data-testid={`stat-link-${c.key}`} aria-label={`Vai a ${c.label}`}>{body}</Link>
    : body;
};

export default function Dashboard() {
  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: async () => (await api.get("/dashboard/stats")).data });
  const { data: interventi = [] } = useQuery({ queryKey: ["interventi-recent"], queryFn: async () => (await api.get("/interventi")).data });
  const { user } = useAuth();
  const isManager = ["admin", "coordinatore"].includes(user?.role);
  const { data: quota } = useQuery({ queryKey: ["quota-report"], queryFn: async () => (await api.get("/guardie/quota-report")).data, enabled: isManager });
  const downloadFile = async (url, filename) => {
    const res = await api.get(url, { responseType: "blob" });
    const href = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = href;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
  };

  return (
    <div className="gz-fade-up">
      <div className="mb-6 flex items-center gap-4">
        <img
          src="/emblem.png"
          alt="Emblema Associazione Nazionale Guardie Ecozoofile ODV"
          className="h-16 w-auto object-contain drop-shadow-sm sm:h-20"
          data-testid="home-emblem"
        />
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">Dashboard</h1>
          <p className="mt-1 text-sm text-slate-500">Associazione Nazionale Guardie Ecozoofile ODV · Panoramica operativa.</p>
        </div>
      </div>

      <ScadenzeBanner />

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {STAT_CARDS.map((c) => (
          <StatCard key={c.key} c={c} value={stats ? stats[c.key] : "—"} clickable={!c.roles || c.roles.includes(user?.role)} />
        ))}
      </div>

      {isManager && <div className="mt-6"><PresentazioneAdmin /></div>}

      {["admin", "coordinatore", "territoriale", "com_distaccamento"].includes(user?.role) && <ConfrontoProvince />}

      {isManager && quota && (
        <Card className="mt-6 border-slate-200 shadow-sm" data-testid="quota-report-card">
          <CardContent className="p-5">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50"><Euro className="h-5 w-5 text-[#166534]" /></div>
                <h2 className="font-heading text-lg font-bold text-slate-900">Quote associative {quota.year}</h2>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" data-testid="quota-export-excel" onClick={() => downloadFile("/guardie/export/excel?stato=attiva&quota=non_pagata", `non_in_regola_${quota.year}.xlsx`)}>
                  <FileDown className="mr-1.5 h-4 w-4" /> Non in regola (Excel)
                </Button>
                <Button variant="outline" size="sm" data-testid="quota-export-pdf" onClick={() => downloadFile("/guardie/export/pdf?stato=attiva&quota=non_pagata", `non_in_regola_${quota.year}.pdf`)}>
                  <FileDown className="mr-1.5 h-4 w-4" /> Non in regola (PDF)
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                <p className="font-heading text-2xl font-extrabold text-green-700" data-testid="quota-pagate">{quota.pagate}</p>
                <p className="text-xs font-medium uppercase tracking-wide text-green-700/70">Quote pagate</p>
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                <p className="font-heading text-2xl font-extrabold text-amber-700" data-testid="quota-non-pagate">{quota.non_pagate}</p>
                <p className="text-xs font-medium uppercase tracking-wide text-amber-700/70">Da sollecitare</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <p className="font-heading text-2xl font-extrabold text-slate-900">{quota.attive}</p>
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Guardie attive</p>
              </div>
            </div>
            <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full bg-[#166534]" style={{ width: `${quota.attive ? (quota.pagate / quota.attive) * 100 : 0}%` }} />
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="mt-6 border-slate-200 shadow-sm">
        <CardContent className="p-5">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-heading text-lg font-bold text-slate-900">Ultimi verbali</h2>
            <Link to="/interventi" className="inline-flex items-center gap-1 text-sm font-medium text-[#166534] hover:underline">
              Vedi tutti <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          {interventi.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-400">Nessun verbale registrato.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {interventi.slice(0, 5).map((i) => (
                <div key={i.id} className="flex items-center justify-between py-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      <span className="font-mono-code text-xs text-slate-500">{i.protocollo}</span> · {i.tipo}
                    </p>
                    <p className="text-xs text-slate-500">
                      {i.comune} — {i.guardia_nome || "—"} · {fmtDateTime(i.data_ora)}
                    </p>
                  </div>
                  <StatoVerbaleBadge stato={i.stato} />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
